/**
 * Notices a session that ended WHILE the portal is open, so the person is taken to sign-in instead
 * of every screen saying "Check your connection and try again".
 *
 * Why (audit R6-9, 2026-10-01): the session is only checked at start-up (/api/user/profile → one
 * refresh → sign-out). When the token expires mid-session — the 7-day expiry, the 30-day sign-in
 * cap, a password change elsewhere — every API answers 401, and only a few feature mappers know
 * what a 401 means; the shell stays signed in and every screen shows a connection error.
 *
 * How: one OBSERVER on window.fetch (it never changes a request or a response). A 401 from one of
 * our /api endpoints, sent with a Bearer token, is reported with that token. AuthProvider then
 * re-checks the session the way start-up does — profile → one refresh → sign-out only if the
 * server refuses both — so a 401 that is really a per-feature refusal costs one profile read and
 * changes nothing.
 *
 * Not reported: /api/auth/* (sign-in failures, refresh and logout answer 401 by design) and
 * /api/user/profile (AuthProvider's own check).
 */
export type UnauthorizedListener = (tokenSent: string) => void;

let listener: UnauthorizedListener | null = null;
/** AuthProvider subscribes; returns the unsubscribe. One subscriber (the provider). */
export function onSessionUnauthorized(l: UnauthorizedListener): () => void {
  listener = l;
  return () => { if (listener === l) listener = null; };
}

const API_PATH = /^\/api\//;
const EXCLUDED = /^\/api\/(auth\/|user\/profile(\/|$|\?))/;
const BEARER = /^\s*bearer\s+(\S+)\s*$/i;

/** The /api path of `url` if it is one of ours (relative, the page origin, or the server origin). */
export function ourApiPath(url: string, origins: readonly string[]): string | null {
  if (typeof url !== "string" || !url) return null;
  let p: string | null = null;
  if (url.startsWith("/") && !url.startsWith("//")) p = url;
  else for (const o of origins) if (o && url.startsWith(o + "/")) { p = url.slice(o.length); break; }
  return p && API_PATH.test(p) ? p : null;
}

/** Pure: the token to report for this response, or null. */
export function unauthorizedToken(status: number, url: string, authHeader: string | null, origins: readonly string[]): string | null {
  if (status !== 401) return null;
  const p = ourApiPath(url, origins);
  if (!p || EXCLUDED.test(p)) return null;
  const m = BEARER.exec(authHeader ?? "");
  const tok = m?.[1];
  return tok && tok !== "null" && tok !== "undefined" ? tok : null;
}

function authHeaderOf(input: RequestInfo | URL, init?: RequestInit): string | null {
  try {
    if (init?.headers) { const h = new Headers(init.headers).get("Authorization"); if (h) return h; }
    if (typeof Request !== "undefined" && input instanceof Request) return input.headers.get("Authorization");
  } catch { /* unreadable headers: nothing to report */ }
  return null;
}

let installed = false;
/**
 * Wraps window.fetch once (web: client/src/main.tsx; app: client-app/src/main.tsx, BEFORE
 * installAppFetch so it sees the token appFetch fills in). `origins`: the page origin and, in the
 * app, the server origin.
 */
export function installSessionWatch(origins: () => readonly string[]): void {
  if (installed || typeof window === "undefined" || typeof window.fetch !== "function") return;
  installed = true;
  const native = window.fetch.bind(window);
  window.fetch = ((input: RequestInfo | URL, init?: RequestInit) => {
    const p = native(input, init);
    return p.then((res) => {
      if (res.status === 401 && listener) {
        try {
          const url = input instanceof URL ? input.href : typeof input === "string" ? input : (input as Request).url;
          const tok = unauthorizedToken(res.status, url, authHeaderOf(input, init), origins());
          if (tok) listener(tok);
        } catch { /* observing must never break a request */ }
      }
      return res;
    });
  }) as typeof window.fetch;
}

/** Test-only. */
export function resetSessionWatchForTests(): void { installed = false; listener = null; }

/** At most one re-check per token in this window (a screen fires several requests at once). */
export const RECHECK_WINDOW_MS = 30_000;

/**
 * Pure: should a reported 401 re-check the session? Only for the token the app holds NOW (a 401 on
 * a token already replaced by a refresh, or on a crew token — CrewApp owns those — is not ours), and
 * not again for the same token within RECHECK_WINDOW_MS.
 */
export function shouldRecheckSession(
  sent: string, current: string | null, currentIsCrew: boolean,
  last: { token: string; at: number } | null, now: number,
): boolean {
  if (!current || sent !== current || currentIsCrew) return false;
  return !(last && last.token === current && now - last.at < RECHECK_WINDOW_MS);
}
